import { useCallback, useEffect, useState } from 'react';
import MyTeamModal from './MyTeamModal';
import { searchTournaments } from '../api/myTeamApi';
import type { ITournamentSearchResult } from '../types/myTeam';

const DEBOUNCE_MS = 1200;

interface SearchTournamentModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSelectTournament: (tournament: ITournamentSearchResult) => void;
}

function SearchTournamentModal({
  open,
  onOpenChange,
  onSelectTournament,
}: SearchTournamentModalProps) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<ITournamentSearchResult[]>([]);
  const [loading, setLoading] = useState(false);
  /** True until the debounce timer fires — avoids showing "no results" while waiting. */
  const [pendingDebounce, setPendingDebounce] = useState(false);

  useEffect(() => {
    if (!query.trim()) {
      setResults([]);
      setPendingDebounce(false);
      setLoading(false);
      return;
    }
    setPendingDebounce(true);
    setResults([]);
    const timer = setTimeout(() => {
      setPendingDebounce(false);
      setLoading(true);
      searchTournaments(query.trim())
        .then((list) => setResults(list))
        .catch(() => setResults([]))
        .finally(() => setLoading(false));
    }, DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [query]);

  const showSearching = pendingDebounce || (loading && results.length === 0 && query.trim());

  const handleTournamentClick = useCallback(
    (tournament: ITournamentSearchResult) => {
      onSelectTournament(tournament);
      setQuery('');
      setResults([]);
      onOpenChange(false);
    },
    [onSelectTournament, onOpenChange]
  );

  return (
    <MyTeamModal
      open={open}
      onOpenChange={onOpenChange}
      title="Search Tournament"
      className="search-tournament-dialog"
    >
      <div className="search-tournament-modal">
        <div className="search-tournament-input-row">
          <input
            type="text"
            placeholder="Tournament Name"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="search-tournament-input"
          />
        </div>
        {showSearching && (
          <p className="search-tournament-loading">Searching...</p>
        )}
        {!loading && results.length > 0 && (
          <div className="search-tournament-results">
            {results.map((tournament) => (
              <div
                key={tournament.tournamentId}
                className="search-tournament-result-card"
                onClick={() => handleTournamentClick(tournament)}
                onKeyDown={(e) => e.key === 'Enter' && handleTournamentClick(tournament)}
                role="button"
                tabIndex={0}
              >
                <span className="search-tournament-card-name">{tournament.tournamentName}</span>
                {tournament.organizer && (
                  <span className="search-tournament-card-organizer">{tournament.organizer}</span>
                )}
              </div>
            ))}
          </div>
        )}
        {!pendingDebounce && !loading && query.trim() && results.length === 0 && (
          <p className="search-tournament-no-results">No tournaments found.</p>
        )}
      </div>
    </MyTeamModal>
  );
}

export default SearchTournamentModal;
